import { FC, useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import { Box, IconButton, MenuItem, MenuList, Typography } from '@mui/material';

import { PricingType } from 'src/hooks/usePricings';
import ProductAndpricing from 'src/pages/ViewPartnerPage/components/ProductAndPricingOfferings/ProductAndPricing';
import { OfferList } from 'src/pages/ViewPartnerPage/components/ProductAndPricingOfferings/ProductAndPricingOfferings';

import Contact, { ContactInformation } from './components/Contact';

export interface Step {
    step: string;
    label: string;
}

interface SubNavigationProps {
    steps: Step[];
    isProductAndPricing?: boolean;
    contactInformation?: ContactInformation[];
    offerList?: OfferList;
    isMerchant?: boolean;
    handleEdit?: (activeStep?: string) => void;
}

const pricingTypeMap = {
    terminal: PricingType.TERMINAL,
    online: PricingType.ONLINE,
    transaction: PricingType.TRANSACTIONAL,
};

const SubNavigation: FC<SubNavigationProps> = ({
    steps,
    isProductAndPricing,
    contactInformation,
    offerList,
    isMerchant,
    handleEdit,
}) => {
    const [activeStep, setActiveStep] = useState<string>(steps[0]?.step);

    const contactInfoMap = steps.reduce((map, { step }, index) => {
        if (contactInformation && contactInformation[index]) {
            map[step] = contactInformation[index];
        }
        return map;
    }, {} as Record<string, ContactInformation>);

    const handleStepClick = (step: string) => {
        setActiveStep(step);
    };

    const renderStepNavItem = (step: string, label: string) => {
        const isActive = activeStep === step;

        return (
            <MenuItem
                key={step}
                selected={isActive}
                onClick={() => {
                    return handleStepClick(step);
                }}
                sx={{
                    display: 'flex',
                    width: '100%',
                    objectFit: 'contain',
                    padding: '0',
                }}
            >
                {handleEdit && isActive && (
                    <IconButton
                        data-testid={`edit-${step}`}
                        sx={{ position: 'absolute', right: 0, top: 0 }}
                        onClick={() => {
                            handleEdit(activeStep);
                        }}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                )}
                <Typography
                    fontSize={isActive ? '16px' : '14px'}
                    sx={{
                        color: isActive ? 'white' : '#686867',
                        fontWeight: isActive ? 500 : 400,
                        bgcolor: (() => {
                            if (isActive) {
                                return isProductAndPricing ? '#FDC800' : '#F18F34';
                            }
                            return 'transparent';
                        })(),
                    }}
                    padding="24px"
                    width="100%"
                    textAlign="center"
                >
                    {contactInfoMap[step]?.isBillingContact ? `*${label}` : `${label}`}
                </Typography>
            </MenuItem>
        );
    };

    const renderContent = (step: string) => {
        const pricingType = pricingTypeMap[step];
        const contactInformation = contactInfoMap[step];

        if (isProductAndPricing && pricingType) {
            const filteredOfferList =
                offerList.length > 0 &&
                offerList.filter((offer) => {
                    return offer.type === pricingType;
                });

            return <ProductAndpricing offerList={filteredOfferList} />;
        }

        if (!contactInformation) {
            return null;
        }

        return (
            <Contact
                contactInformation={contactInformation}
                isMerchant={isMerchant}
                activeStep={activeStep}
            />
        );
    };

    return (
        <>
            <MenuList
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    borderTop: '0.5px solid #DADADA',
                    padding: '0',
                    listStyle: 'none',
                    overflowX: steps.length > 3 ? 'auto' : 'hidden',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    width: '100%',
                    scrollbarWidth: 'thin',
                }}
            >
                {steps.map(({ step, label }) => {
                    return renderStepNavItem(step, label);
                })}
            </MenuList>
            <Box>{renderContent(activeStep)}</Box>
        </>
    );
};

export default SubNavigation;
